// Compose → preview → send, 한 사이트에 지금 보내는 알림 메일.
//
// SendInvitationModal의 축소판이고, 빠진 것들이 이 화면의 성격을 말해 준다. 비밀번호를
// 발급하지 않으므로 PI용/CRC용 본문이 갈리지 않고(둘이 같은 메일을 받는다), 만료일을
// 갱신하지 않으므로 "재발송하면 날짜가 옮겨집니다" 경고가 없고, 버전을 고정하지 않으므로
// 미발행 변경사항 경고도 없다. 알림은 아무것도 바꾸지 않는 메일이다.
//
// 재발송처럼 지난 알림 본문을 되열어 준다: 같은 사이트에 두 번째 재촉을 보낼 때 첫 번째와
// 다른 말을 하게 되는 것이 손으로 다시 쓰는 흐름의 실패 방식이다.
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { sfqApi } from "../../../api/feasibility";
import EmailComposer from "./EmailComposer";
import {
  DEFAULT_REMINDER_TEMPLATES, REMINDER_TEMPLATE_VARS,
  buildReminderVars, hasRequiredVars, hasPasswordVar,
} from "./reminderTemplates";
import {
  Backdrop, Modal, Header, Title, CloseBtn, Body, Footer, Btn,
  LangTabs, LangTab, Loading, Warning,
} from "./emailModalUI";

const To = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 6px;
  b { color: #212529; font-weight: 600; }
`;

const CrcToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  color: ${(p) => (p.$disabled ? "#adb5bd" : "#495057")};
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  input { cursor: inherit; margin: 0; }
`;

const Hint = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  background: #f8f9fa;
  font-size: 11px;
  line-height: 1.6;
  color: #6c757d;
  b { color: #495057; }
`;

const Reopened = styled.div`
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  background: #e7f1ff;
  border-left: 3px solid #0d6efd;
  font-size: 12px;
  color: #084298;
  line-height: 1.5;
  b { font-weight: 600; }
`;

const LANGS = [
  { key: "ko", label: "한국어" },
  { key: "en", label: "English" },
];

const fmtSentAt = (v) =>
  v ? new Date(v).toLocaleString("ko-KR", {
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
      })
    : "";

export default function SendReminderModal({
  sfqId, inv, publicUrl, fromName, onSend, onClose,
}) {
  const [lang, setLang]           = useState("ko");
  const [templates, setTemplates] = useState(null);
  const [drafts, setDrafts]       = useState(null);
  const [sending, setSending]     = useState(false);
  const [reopened, setReopened]   = useState(null);
  const [includeCrc, setIncludeCrc] = useState(true);

  // 세 가지를 함께 읽는다: 스터디의 알림 템플릿, 이 사이트에 마지막으로 보낸 알림,
  // 그리고 마지막으로 보낸 초대.
  //
  // 뒤의 둘은 서로 다른 일을 한다. 본문은 지난 "알림"에서만 되열린다 — 초대 본문에는
  // 비밀번호 안내가 들어 있어 알림으로는 보낼 수 없는 글이다. 반면 열리는 언어 탭은
  // 둘 중 더 최근에 나간 메일을 따른다: 영어로 초대받은 사이트에 한국어 탭이 열리면
  // CRA가 눈치채지 못한 채 다른 언어로 재촉하게 되고, 재발송(초대)이 알림보다 나중일
  // 수도 있으므로 날짜를 비교해야 한다. 서버(reminder.js)가 자동 발송에서 고르는 언어와
  // 같은 규칙이다 — siteLang.js의 readInvitationLang.
  useEffect(() => {
    let alive = true;
    const invId = inv.id;

    Promise.all([
      sfqApi.getReminderTemplate(sfqId).then((r) => r.data.data || {}).catch(() => ({})),
      sfqApi.getLastReminderMail(sfqId, invId).then((r) => r.data.data || null).catch(() => null),
      sfqApi.getLastSentMail(sfqId, invId).then((r) => r.data.data || null).catch(() => null),
    ]).then(([saved, last, lastInvite]) => {
      if (!alive) return;

      const seed = {
        ko: saved.ko ? { subject: saved.ko.subject, bodyHtml: saved.ko.bodyHtml }
                     : { ...DEFAULT_REMINDER_TEMPLATES.ko },
        en: saved.en ? { subject: saved.en.subject, bodyHtml: saved.en.bodyHtml }
                     : { ...DEFAULT_REMINDER_TEMPLATES.en },
      };
      setTemplates(seed);

      // 이 사이트가 마지막으로 받은 메일의 언어. 언어가 기록되기 전의 발송은 lang이
      // 비어 있으므로 세지 않는다(readInvitationLang의 IS NOT NULL 필터와 같다).
      const dated = [last, lastInvite].filter((m) => m?.lang);
      dated.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
      const siteLang = dated[0]?.lang === "en" ? "en" : "ko";
      setLang(siteLang);

      const next = { ko: { ...seed.ko }, en: { ...seed.en } };
      if (last?.bodyHtml) {
        const prevLang = last.lang === "en" ? "en" : "ko";
        next[prevLang] = {
          subject:  last.subject || seed[prevLang].subject,
          bodyHtml: last.bodyHtml,
        };
        // 되열린 본문이 있는 탭이 지금 열린 탭과 다를 수 있다(그 뒤 다른 언어로 재발송된
        // 경우). 그때는 안내 문구도 그 탭에서만 보이게 두는 편이 맞다 — 아래 렌더가
        // reopened.lang === lang 일 때만 띄운다.
        setReopened({ lang: prevLang, sentAt: last.sentAt });
      }
      setDrafts(next);
    });

    return () => { alive = false; };
  }, [sfqId, inv.id]);

  function patch(next) {
    setDrafts((d) => ({ ...d, [lang]: { ...d[lang], ...next } }));
  }

  function resetToTemplate() {
    if (!window.confirm("편집한 내용을 버리고 알림 템플릿 원본으로 되돌립니다. 계속할까요?")) return;
    setDrafts((d) => ({ ...d, [lang]: { ...templates[lang] } }));
    setReopened((r) => (r && r.lang === lang ? null : r));
  }

  const vars = useMemo(
    () => buildReminderVars({ inv, publicUrl, fromName, lang }),
    [inv, publicUrl, fromName, lang]
  );

  const norm      = (v) => String(v || "").trim().toLowerCase();
  const crcIsPi   = !!inv.crcEmail && norm(inv.crcEmail) === norm(inv.piEmail);
  const sendToCrc = !!inv.crcEmail && !crcIsPi && includeCrc;

  const draft = drafts?.[lang];
  // 비밀번호 변수는 서버가 채울 수 없다. 저장 때도 막지만, 이전에 저장된 템플릿이나 되열린
  // 본문에 남아 있을 수 있으므로 발송 직전에도 본다.
  const hasPw = hasPasswordVar(draft?.bodyHtml) || hasPasswordVar(draft?.subject);
  const canSend = !!draft?.subject?.trim() && hasRequiredVars(draft?.bodyHtml)
                  && !hasPw && !!inv.piEmail && !sending;

  async function handleSend() {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend({ subject: draft.subject, bodyHtml: draft.bodyHtml, lang, includeCrc });
    } finally {
      setSending(false);
    }
  }

  return (
    <Backdrop>
      <Modal $wide>
        <Header>
          <Title>
            알림 메일 발송 — {inv.siteName || inv.siteCode || `#${inv.id}`}
          </Title>
          <CloseBtn onClick={onClose} disabled={sending}>×</CloseBtn>
        </Header>

        <Body>
          <To>받는 사람: <b>{inv.piEmail || "(PI 이메일 없음)"}</b></To>
          <To>참조: <b>{sendToCrc ? inv.crcEmail : "(없음)"}</b></To>

          <CrcToggle $disabled={!inv.crcEmail || crcIsPi || sending}>
            <input
              type="checkbox"
              checked={sendToCrc}
              disabled={!inv.crcEmail || crcIsPi || sending}
              onChange={(e) => setIncludeCrc(e.target.checked)}
            />
            CRC를 참조(Cc)로 추가
            {!inv.crcEmail
              ? " (등록된 CRC 이메일 없음)"
              : crcIsPi
                ? ` (${inv.crcEmail} — PI와 같은 주소라 참조하지 않습니다)`
                : ` (${inv.crcEmail})`}
          </CrcToggle>

          <Hint>
            알림 메일에는 비밀번호가 들어가지 않으므로 <b>PI와 CRC가 같은 내용</b>을 받습니다.
            (초대 메일은 비밀번호 때문에 두 사람의 본문이 달랐습니다.)
          </Hint>

          <Warning>
            이 메일은 <b>새 비밀번호를 발급하지 않습니다.</b> 사이트는 처음 받은 초대 메일의
            비밀번호를 그대로 쓰며, 작성 중이던 답변과 링크 만료일도 그대로입니다.
            비밀번호를 새로 주려면 이 창을 닫고 <b>Resend</b>를 쓰세요.
          </Warning>

          {!inv.piEmail && (
            <Warning>
              이 사이트에는 <b>PI 이메일</b>이 등록되어 있지 않아 발송할 수 없습니다.
              Edit에서 PI 이메일을 입력한 뒤 다시 시도해 주세요.
            </Warning>
          )}

          {hasPw && (
            <Warning>
              본문에 <b>{"{{password}}"}</b> 변수가 있습니다. 알림 메일은 비밀번호를 발급하지 않아
              이 변수는 채워지지 않으며, 지운 뒤에야 발송할 수 있습니다.
            </Warning>
          )}

          {reopened && reopened.lang === lang && (
            <Reopened>
              <b>{fmtSentAt(reopened.sentAt)}</b>에 이 사이트로 보낸 알림 메일 내용을 불러왔습니다.
              그대로 다시 보내거나 수정한 뒤 보내실 수 있으며, 스터디 템플릿으로 되돌리려면
              아래 <b>템플릿 원본으로</b>를 누르세요.
            </Reopened>
          )}

          <LangTabs>
            {LANGS.map((l) => (
              <LangTab key={l.key} type="button" $active={lang === l.key} onClick={() => setLang(l.key)}>
                {l.label}
              </LangTab>
            ))}
          </LangTabs>

          {!drafts ? (
            <Loading>알림 템플릿을 불러오는 중...</Loading>
          ) : (
            <EmailComposer
              sfqId={sfqId}
              subject={draft.subject}
              bodyHtml={draft.bodyHtml}
              onChange={patch}
              vars={vars}
              varList={REMINDER_TEMPLATE_VARS}
            />
          )}
        </Body>

        <Footer>
          <Btn type="button" onClick={resetToTemplate} disabled={!drafts || sending}>템플릿 원본으로</Btn>
          <Btn type="button" onClick={onClose} disabled={sending}>취소</Btn>
          <Btn type="button" $primary onClick={handleSend} disabled={!canSend}>
            {sending ? "발송 중..." : "알림 발송"}
          </Btn>
        </Footer>
      </Modal>
    </Backdrop>
  );
}
